import type { ReactNode } from "react";
import { Button } from "primereact/button";

import type { EmbarcacaoDetailStatus } from "@/modules/embarcacoes";
import { EmptyState } from "@/screens/control-panel/components/empty-state";
import type { EmptyStateContent } from "@/screens/control-panel/components/empty-state";
import { StateMessage } from "@/screens/control-panel/components/state-message";
import { EMPTY_FIELD_TEXT, formatDetailDate } from "../embarcacao-detail-format";
import styles from "./detail-section.module.scss";

export const DETAIL_SECTION_RETRY_LABEL = "Tentar novamente";
export const DETAIL_SECTION_REAUTH_LABEL = "Entrar novamente";

/** Prefixo do texto da data de atualização, ao lado do título (protótipo). */
export const LAST_UPDATE_PREFIX = "Última Atualização";

interface DetailSectionProps {
  /** Âncora da seção no corpo do modal; o menu lateral rola até ela (AC2). */
  readonly id: string;
  readonly title: string;
  /**
   * Data da última atualização das informações da seção. `undefined` enquanto
   * não há dados a apresentar — nos estados de exceção só o título permanece.
   */
  readonly ultimaAtualizacao?: string | null;
  readonly testId: string;
  readonly children: ReactNode;
}

/**
 * Casca comum das seções do detalhamento: título, data de atualização,
 * divisória e o corpo da seção. Uma seção nova só precisa preencher o corpo.
 */
export function DetailSection({
  id,
  title,
  ultimaAtualizacao,
  testId,
  children,
}: DetailSectionProps) {
  const headingId = `${id}-titulo`;

  return (
    <section
      className={styles.section}
      // A casca localiza a seção pelo atributo para rolar o corpo até ela.
      data-section-id={id}
      aria-labelledby={headingId}
      data-testid={testId}
    >
      <header className={styles.header}>
        <h2 className={styles.title} id={headingId}>
          {title}
        </h2>

        {ultimaAtualizacao === undefined ? null : (
          <p className={styles.lastUpdate} data-testid={`${testId}-ultima-atualizacao`}>
            {`${LAST_UPDATE_PREFIX}: ${
              ultimaAtualizacao === null ? EMPTY_FIELD_TEXT : formatDetailDate(ultimaAtualizacao)
            }`}
          </p>
        )}
      </header>

      <hr className={styles.divider} />

      {children}
    </section>
  );
}

/**
 * Campos da seção em duas colunas (AC3): o grid acomoda o par rótulo + valor e
 * reorganiza-se em coluna única nas larguras menores (AC6).
 */
export function DetailSectionFields({
  testId,
  children,
}: {
  readonly testId: string;
  readonly children: ReactNode;
}) {
  return (
    <dl className={styles.fields} data-testid={testId}>
      {children}
    </dl>
  );
}

interface DetailFieldProps {
  readonly label: string;
  /** Valor já formatado; `null` (ou vazio) vira "Campo sem preenchimento" (AC4). */
  readonly value: string | null;
  /**
   * Abre uma linha nova do grid, mantendo o campo na coluna da esquerda. O
   * protótipo agrupa certos pares na mesma linha; sem isso o preenchimento
   * corrido deslocaria todos os campos seguintes de coluna.
   */
  readonly startsRow?: boolean;
  readonly testId: string;
}

/** Par rótulo + valor, somente leitura: nenhum campo de entrada (RN003). */
export function DetailField({ label, value, startsRow = false, testId }: DetailFieldProps) {
  return (
    <div className={`${styles.field} ${startsRow ? styles.fieldRowStart : ""}`}>
      <dt className={styles.label}>{label}</dt>
      <dd className={styles.value} data-testid={testId}>
        {value === null || value === "" ? EMPTY_FIELD_TEXT : value}
      </dd>
    </div>
  );
}

interface DetailSectionStateProps {
  readonly status: Exclude<EmbarcacaoDetailStatus, "success">;
  readonly loadingMessage: string;
  readonly errorMessage: string | null;
  /** Padrão global de estado vazio do Hub, quando a seção não tem informações. */
  readonly emptyContent: EmptyStateContent;
  readonly onReauthenticate: () => void;
  readonly onRetry: () => void;
  readonly testId: string;
}

const STATE_ICONS: Record<string, string> = {
  loading: "pi pi-spinner pi-spin",
  sessionExpired: "pi pi-lock",
  forbidden: "pi pi-lock",
  notFound: "pi pi-search-minus",
  error: "pi pi-exclamation-triangle",
};

/**
 * Carregamento e cenários de exceção da seção (AC5). Ocupam o lugar dos campos
 * dentro do modal: em nenhum deles um campo parcial é exibido.
 */
export function DetailSectionState({
  status,
  loadingMessage,
  errorMessage,
  emptyContent,
  onReauthenticate,
  onRetry,
  testId,
}: DetailSectionStateProps) {
  if (status === "empty") {
    return <EmptyState kind="empty" content={emptyContent} testId={`${testId}-empty`} />;
  }

  return (
    <StateMessage
      icon={STATE_ICONS[status] ?? "pi pi-exclamation-triangle"}
      title={status === "loading" ? loadingMessage : (errorMessage ?? "")}
      testId={`${testId}-${status}`}
    >
      {status === "sessionExpired" ? (
        <Button
          type="button"
          className={styles.stateAction}
          label={DETAIL_SECTION_REAUTH_LABEL}
          icon="pi pi-sign-in"
          onClick={onReauthenticate}
          data-testid={`${testId}-reauth`}
        />
      ) : null}

      {status === "error" ? (
        <Button
          type="button"
          className={styles.stateAction}
          label={DETAIL_SECTION_RETRY_LABEL}
          icon="pi pi-refresh"
          onClick={onRetry}
          data-testid={`${testId}-retry`}
        />
      ) : null}
    </StateMessage>
  );
}
